import { ThemeType } from "@intelligems/ig-types/schemas/organization";
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@src/store/rootState";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { getThemes as _getThemes } from "@src/store/slices/themes";
import { themeUpdatedAtMs } from "@src/utils/themeUpdatedAt";

const { isAuthenticated } = experienceBuilderSlice.selectors;

// Sort themes: live theme first, then most recently updated.
export const getAuthenticatedThemes = createSelector(_getThemes, (themes) => {
  return [...themes].sort((a, b) => {
    const aIsLive = a.role === "main";
    const bIsLive = b.role === "main";

    // Live theme always comes first
    if (aIsLive && !bIsLive) {
      return -1;
    }
    if (bIsLive && !aIsLive) {
      return 1;
    }

    // Then sort by most recently updated
    return themeUpdatedAtMs(b) - themeUpdatedAtMs(a);
  });
});

// Stable reference so the empty case doesn't re-render consumers on each call.
const NO_THEMES: ThemeType[] = [];

/**
 * A demo URL points the builder at a store the logged-in org's admin token
 * can't reach, so we can't list its themes — there are none to choose from.
 */
export const getUnauthenticatedThemes = (): ThemeType[] => NO_THEMES;

export const getThemes = (state: RootState): ThemeType[] =>
  isAuthenticated(state)
    ? getAuthenticatedThemes(state)
    : getUnauthenticatedThemes();
